import { Injectable, inject } from '@angular/core';
import { CultureLanguageMap, HttpService, UrlService } from '@boels/helpers';
import {
  BoelsTeaserComponentData,
  CMSTabParagraphContainer,
  CardsGroupComponentData,
  ComponentResponseModel,
  AppConfig,
  ParamsModel,
  ProductSearchPage,
  TeasersGroupComponentData,
} from '@boels/models';
import {
  Observable,
  catchError,
  combineLatest,
  filter,
  map,
  of,
  switchMap,
  take,
  throwError,
  withLatestFrom,
} from 'rxjs';
import { CultureFacade } from '../cultures/facade/cultures.facade';
import { SiteContextFacade } from '../site-context/facade/site-context.facade';

@Injectable({
  providedIn: 'root',
})
export class ComponentDataService {
  private httpService = inject(HttpService);
  private urlService = inject(UrlService);
  private siteContextFacade = inject(SiteContextFacade);
  private cultureFacade = inject(CultureFacade);
  private environment = inject<AppConfig>('ENVIRONMENT' as any);

  activeBaseSiteUid: string | undefined;

  getCMSChildrenData(
    componentData:
      | CardsGroupComponentData
      | TeasersGroupComponentData
      | CMSTabParagraphContainer,
    params?: any,
  ): Observable<BoelsTeaserComponentData[]> {
    const childIds: string[] = (
      (componentData as TeasersGroupComponentData)?.teasers ??
      (componentData as CardsGroupComponentData)?.cards ??
      (componentData as CMSTabParagraphContainer)?.components ??
      componentData?.children ??
      ''
    ).split(' ');
    return this.getComponents(childIds.join(','), params).pipe(
      map((response: ComponentResponseModel) =>
        childIds.map((id) => response.component.find((el) => el.uid === id)),
      ),
      switchMap((components) => {
        const productLinkIds = components
          .map((component: any) => component?.link?.productCode)
          .filter(Boolean);

        if (!productLinkIds.length) return of(components);

        return this.getComponentsWithProductLinks(productLinkIds, components);
      }),
    ) as Observable<BoelsTeaserComponentData[]>;
  }

  getComponents(
    componentIds: string,
    params?: any,
  ): Observable<ComponentResponseModel> {
    return this.cultureFacade.getActiveCulture().pipe(
      filter(Boolean),
      withLatestFrom(
        this.siteContextFacade.getActiveBaseSiteEntity(),
        this.cultureFacade.getActiveLanguage(),
        this.cultureFacade.getActiveCurrency(),
      ),
      switchMap(([, activeBaseSite, lang, currency]) => {
        this.activeBaseSiteUid = activeBaseSite.uid;
        const url =
          this.urlService.combineUri(activeBaseSite.uid, 'cms', 'components') +
          `?componentIds=${componentIds}&lang=${lang}&curr=${currency}`;
        return this.httpService.get<ComponentResponseModel>(url, params);
      }),
      take(1),
    );
  }

  getImageSrc(imageSrc: string) {
    const { baseUrl } = this.environment.backend.occ;
    return `${baseUrl}${imageSrc}`;
  }

  getComponentsWithProductLinks(productLinkIds: string[], components: any) {
    return combineLatest([
      this.cultureFacade.getActiveCulture(),
      this.cultureFacade.getActiveLanguage(),
      this.cultureFacade.getActiveCurrency(),
    ]).pipe(
      switchMap(([culture, lang, currency]) => {
        const queryParams: ParamsModel = [
          ['fields', 'products(code,url)'],
          ['query', productLinkIds.join(' ')],
          ['lang', culture == CultureLanguageMap['en-gb'] ? 'en_GB' : lang],
          ['curr', currency],
        ];
        return this.httpService
          .get<ProductSearchPage>(
            `${this.activeBaseSiteUid}/products/search`,
            queryParams,
          )
          .pipe(
            map((productLinks: ProductSearchPage) =>
              components.map((component: any) => {
                component.link ??= {};
                component.link.url =
                  productLinks?.products?.find(
                    (link) => link.code === component?.link?.productCode,
                  )?.url ||
                  component.link.url ||
                  '/';
                return component;
              }),
            ),
            catchError((error) => {
              throwError(() => error);
              return of([]);
            }),
          );
      }),
    );
  }
}
